import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { EmptyState, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { ScopeField, scopeFromKey } from '../people/peopleShared.jsx';
import { ROLE_SUGGESTIONS, appointmentText } from './supplierShared.js';
import { checkDomain, checkEmail, checkPhone, checkRequired, checkUrl, cleanDomain, cleanedHint } from '../../utils/validate.js';

function AddSupplierModal({ base, data, onClose, onAdded }) {
  const [form, setForm] = useState({
    name: '', category: 'housing_bureau', website: '', email_from_website: true, email: '', phone: '', booking_url: '',
    role_label: ROLE_SUGGESTIONS.housing_bureau, scope: 'company', is_public: true, notes: '',
  });
  const [roleTouched, setRoleTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const setCategory = (c) => setForm((f) => ({ ...f, category: c, role_label: roleTouched ? f.role_label : (ROLE_SUGGESTIONS[c] || '') }));

  const blur = (k, problem) => setErrors((e) => ({ ...e, [k]: problem || undefined }));
  const blurWebsite = () => {
    if (!form.website.trim()) return;
    const problem = checkDomain(form.website);
    if (problem) blur('website', problem); else set('website', cleanDomain(form.website));
  };

  const save = async () => {
    const found = Object.fromEntries(Object.entries({
      name: checkRequired(form.name, 'the supplier’s name', { max: 200 }),
      role_label: checkRequired(form.role_label, 'what they’re official for, e.g. “Official housing bureau”', { max: 200 }),
      website: checkDomain(form.website, { required: false })
        || (![form.website, form.email, form.phone, form.booking_url].some((v) => v.trim()) ? 'Add at least their website, an email address or a phone number, so people have something to check.' : ''),
      email: checkEmail(form.email, { required: false }),
      phone: checkPhone(form.phone, { required: false }),
      booking_url: checkUrl(form.booking_url, { required: false }),
      notes: form.notes.length > 1000 ? 'Keep notes to 1000 characters or fewer.' : '',
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true); setError('');
    try {
      const res = await api(base, { method: 'POST', body: { ...form, scope: scopeFromKey(form.scope) } });
      onAdded(res);
    } catch (err) {
      setErrors(err.data?.fields || {});
      setError(err.data?.fields ? '' : err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Add an official supplier" onClose={onClose} onSubmit={save} busy={busy} size="lg"
      description="A company that officially works on your events — housing, contractors, the official directory. Checks of their details will say they’re official, and in what role."
      footer={<ModalActions submitLabel="Add supplier" busyLabel="Adding…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="row-2">
        <Field label="Supplier name" required maxLength={200} value={form.name} onChange={(e) => set('name', e.target.value)} error={errors.name} placeholder="ABC Housing Ltd" />
        <Field label="What they do" as="select" value={form.category} onChange={(e) => setCategory(e.target.value)} error={errors.category}>
          {Object.entries(data.categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Field>
      </div>
      <div className="row-2">
        <Field label="Their official role" required maxLength={200} value={form.role_label} onChange={(e) => { setRoleTouched(true); set('role_label', e.target.value); }}
          error={errors.role_label} placeholder="Official housing bureau" hint={!errors.role_label ? 'What people are told, e.g. “Official housing bureau”.' : undefined} />
        <ScopeField value={form.scope} onChange={(v) => set('scope', v)} scopes={data.scopes} companyName={data.organisation.name} error={errors.scope} />
      </div>
      <p className="muted small field-note">How people can check them: add at least one of their website, an email address or a phone number.</p>
      <div className="row-2">
        <Field label="Website" optional value={form.website} onChange={(e) => set('website', e.target.value)} onBlur={blurWebsite} error={errors.website}
          placeholder="abchousing.com" inputMode="url" autoCapitalize="none" spellCheck={false}
          hint={!errors.website ? cleanedHint(form.website) || undefined : undefined} />
        <Field label="Booking link" optional value={form.booking_url} onChange={(e) => set('booking_url', e.target.value)} error={errors.booking_url}
          onBlur={() => form.booking_url.trim() && blur('booking_url', checkUrl(form.booking_url))}
          placeholder="https://abchousing.com/techsummit" inputMode="url" autoCapitalize="none" spellCheck={false}
          hint={!errors.booking_url ? 'The exact page your attendees book through.' : undefined} />
      </div>
      {form.website.trim() && (
        <label className="checkbox">
          <input type="checkbox" checked={form.email_from_website} onChange={(e) => set('email_from_website', e.target.checked)} />
          <span>Their email comes from this domain too</span>
        </label>
      )}
      <div className="row-2">
        <Field label="Email address" optional type="email" autoCapitalize="none" value={form.email} onChange={(e) => set('email', e.target.value)} error={errors.email}
          onBlur={() => form.email.trim() && blur('email', checkEmail(form.email))}
          hint={!errors.email ? 'Only if they use an address on another domain.' : undefined} />
        <Field label="Phone number" optional type="tel" inputMode="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} error={errors.phone}
          onBlur={() => form.phone.trim() && blur('phone', checkPhone(form.phone))}
          hint={!errors.phone ? 'Include the country code if it’s outside the UK.' : undefined} />
      </div>
      <label className="checkbox">
        <input type="checkbox" checked={form.is_public} onChange={(e) => set('is_public', e.target.checked)} />
        <span>Show them on the verify page as an official supplier <span className="muted small">(their website, booking link and phone, with their role)</span></span>
      </label>
      <Field label="Notes" optional hint="Only your team sees these." as="textarea" rows={2} maxLength={1000} counter value={form.notes} onChange={(e) => set('notes', e.target.value)} error={errors.notes} />
    </Modal>
  );
}

export default function Suppliers() {
  const { organisation: current } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const base = current ? `/portal/organisations/${current.id}/suppliers` : '';

  const load = useCallback(async () => {
    if (!current) return;
    try { setData(await api(base)); } catch (err) { setError(err.message); }
  }, [base, current]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="page"><PageHeader title="Official suppliers" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const canManage = data.organisation.can_manage;
  const addButton = canManage && <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>Add supplier</button>;

  return (
    <div className="page">
      <PageHeader title="Official suppliers"
        subtitle="Companies that officially work on your events. Fake “official partners” are a common scam: listing the real ones lets anyone check a call or email in seconds."
        actions={data.suppliers.length > 0 && addButton} />
      <Section flush={data.suppliers.length > 0}>
        {data.suppliers.length === 0 ? (
          <EmptyState title="No suppliers listed yet" action={addButton}>
            Add your official housing bureau, contractors and directory publisher. Then anyone contacted by someone claiming to be one can check it.
          </EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Supplier</th><th>Official for</th><th>Contact details</th><th>Verify page</th></tr></thead>
              <tbody>
                {data.suppliers.map((s) => (
                  <tr key={s.id}>
                    <td data-label="Supplier"><Link className="strong-link" to={`/company/suppliers/${s.id}`}>{s.name}</Link><div className="muted small">{s.category_label}</div></td>
                    <td data-label="Official for">{s.appointments.length ? s.appointments.map(appointmentText).join('; ') : <span className="muted">Nothing</span>}</td>
                    <td data-label="Contact details">{s.identifier_count ? `${s.identifier_count} listed` : <span className="muted">None yet</span>}</td>
                    <td data-label="Verify page">{s.appointments.some((a) => a.is_public) ? <Pill tone="info">Listed</Pill> : <Pill>Not listed</Pill>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      {adding && <AddSupplierModal base={base} data={data} onClose={() => setAdding(false)} onAdded={(res) => navigate(`/company/suppliers/${res.supplier.id}`, { state: { notice: 'Supplier added.' } })} />}
    </div>
  );
}
